import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardCheck, Check, Search, Pencil, X, Save, FolderOpen, Mail, ArrowDownLeft, ArrowUpRight, Trash2 } from 'lucide-react';
import { preinscriptionsApi } from '../../utils/api';
import PreinscriptionDossier from './PreinscriptionDossier';
import { useApp } from '../../context/AppContext';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const ETAPES = [
  { key: 'date_demande_site', label: 'Demande site', short: 'Site' },
  { key: 'date_appel', label: 'Appel tél.', short: 'Appel' },
  { key: 'date_devis', label: 'Devis', short: 'Devis' },
  { key: 'date_visite', label: 'Visite', short: 'Visite' },
  { key: 'date_devis_signe', label: 'Devis signé', short: 'Signé' },
  { key: 'date_convention_signee', label: 'Convention signée', short: 'Conv.' },
  { key: 'date_reception_cheques', label: 'Chèques reçus', short: 'Chèques' },
];

const STATUT_MAP = {
  demande: { label: 'Demande', color: 'bg-gray-100 text-gray-600' },
  appel: { label: 'Appelé', color: 'bg-navy-100 text-navy-700' },
  devis: { label: 'Devis envoyé', color: 'bg-amber-100 text-amber-700' },
  visite: { label: 'Visite faite', color: 'bg-navy-100 text-navy-700' },
  devis_signe: { label: 'Devis signé', color: 'bg-cyan-100 text-cyan-700' },
  convention: { label: 'Convention', color: 'bg-navy-100 text-navy-700' },
  finalise: { label: 'Finalisé', color: 'bg-green-100 text-green-700' },
  annule: { label: 'Annulé', color: 'bg-red-100 text-red-600' },
};

function formatDate(d) {
  if (!d) return null;
  try { return format(parseISO(d), 'd MMM yy', { locale: fr }); }
  catch { return d; }
}

function EditRow({ preinscription, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...preinscription });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await preinscriptionsApi.update(preinscription.id, form);
      onSaved();
    } catch (err) {
      alert('Erreur: ' + (err.response?.data?.error || err.message));
    }
    setSaving(false);
  };

  return (
    <tr className="bg-navy-50/50">
      <td className="px-3 py-2 text-sm font-medium text-gray-900">
        {preinscription.enfant_prenom} {preinscription.enfant_nom}
        <div className="text-xs text-gray-400">{preinscription.parent1_prenom} {preinscription.parent1_nom}</div>
      </td>
      <td className="px-2 py-2 text-xs text-gray-500">{preinscription.structure_nom}</td>
      <td className="px-2 py-2">
        <input type="text" value={form.jours_souhaites || ''} onChange={e => upd('jours_souhaites', e.target.value)}
          className="form-input text-xs py-1 w-24" placeholder="L,Ma,Me..." />
      </td>
      {ETAPES.map(et => (
        <td key={et.key} className="px-1 py-2">
          <input type="date" value={form[et.key] || ''} onChange={e => upd(et.key, e.target.value)}
            className="form-input text-xs py-0.5 w-28" />
        </td>
      ))}
      <td className="px-2 py-2">
        <input type="text" value={form.numeros_cheques || ''} onChange={e => upd('numeros_cheques', e.target.value)}
          className="form-input text-xs py-1 w-24" placeholder="N° chèques" />
      </td>
      <td className="px-2 py-2 text-center text-xs text-gray-300">—</td>
      <td className="px-2 py-2">
        <select value={form.statut} onChange={e => upd('statut', e.target.value)} className="form-select text-xs py-1">
          {Object.entries(STATUT_MAP).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          <button onClick={handleSave} disabled={saving}
            className="p-1 rounded hover:bg-green-100 text-green-600"><Save size={14} /></button>
          <button onClick={onCancel}
            className="p-1 rounded hover:bg-gray-200 text-gray-400"><X size={14} /></button>
        </div>
      </td>
    </tr>
  );
}

export default function PreinscriptionsPage() {
  const { getApiParams, structures } = useApp();
  const [preinscriptions, setPreinscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [dossierId, setDossierId] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStructure, setFilterStructure] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = getApiParams();
    if (filterStructure) params.structure_id = filterStructure;
    preinscriptionsApi.getAll(params)
      .then(res => setPreinscriptions(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getApiParams, filterStructure]);

  useEffect(() => { load(); }, [load]);

  const supprimer = async (p) => {
    if (!window.confirm(`Supprimer la pré-inscription de ${p.enfant_prenom || ''} ${p.enfant_nom || ''} ?\n(La fiche enfant n'est pas supprimée.)`)) return;
    try { await preinscriptionsApi.delete(p.id); load(); }
    catch (err) { alert('Erreur : ' + (err.response?.data?.error || err.message)); }
  };

  const filtered = preinscriptions.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (p.enfant_nom || '').toLowerCase().includes(s) ||
      (p.enfant_prenom || '').toLowerCase().includes(s) ||
      (p.parent1_nom || '').toLowerCase().includes(s) ||
      (p.parent1_prenom || '').toLowerCase().includes(s)
    );
  });

  const getProgress = (p) => {
    let done = 0;
    ETAPES.forEach(et => { if (p[et.key]) done++; });
    return done;
  };

  return (
    <div className="space-y-4">
      {dossierId && (
        <PreinscriptionDossier
          preinscriptionId={dossierId}
          onClose={() => setDossierId(null)}
          onSaved={load}
        />
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          <ClipboardCheck size={20} className="text-cyan-500" /> Pipeline pré-inscriptions
        </h2>
        <div className="flex items-center gap-3">
          <select value={filterStructure} onChange={e => setFilterStructure(e.target.value)}
            className="form-select text-sm w-52">
            <option value="">Toutes les structures</option>
            {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              className="form-input pl-8 text-sm w-56" placeholder="Rechercher famille..." />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total dossiers', value: preinscriptions.length, color: 'text-gray-700' },
          { label: 'En cours', value: preinscriptions.filter(p => !['finalise', 'annule'].includes(p.statut)).length, color: 'text-navy-700' },
          { label: 'Finalisés', value: preinscriptions.filter(p => p.statut === 'finalise').length, color: 'text-green-600' },
          { label: 'Annulés', value: preinscriptions.filter(p => p.statut === 'annule').length, color: 'text-red-500' },
        ].map((s, i) => (
          <div key={i} className="card p-3 text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <ClipboardCheck size={40} className="text-gray-300 mx-auto mb-3" />
            <div className="text-gray-400 text-sm">Aucune pré-inscription</div>
            <p className="text-xs text-gray-300 mt-1">Les pré-inscriptions sont créées automatiquement lors de l'édition d'un devis.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-3 py-2.5 font-semibold text-gray-600 min-w-[140px]">Famille</th>
                  <th className="text-left px-2 py-2.5 font-semibold text-gray-600">Structure</th>
                  <th className="text-left px-2 py-2.5 font-semibold text-gray-600">Jours</th>
                  {ETAPES.map(et => (
                    <th key={et.key} className="text-center px-1 py-2.5 font-semibold text-gray-600 text-xs min-w-[70px]">
                      {et.short}
                    </th>
                  ))}
                  <th className="text-left px-2 py-2.5 font-semibold text-gray-600 text-xs">N° chèques</th>
                  <th className="text-center px-2 py-2.5 font-semibold text-gray-600 text-xs min-w-[90px]">E-mails</th>
                  <th className="text-left px-2 py-2.5 font-semibold text-gray-600">Statut</th>
                  <th className="text-right px-2 py-2.5 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => {
                  if (editingId === p.id) {
                    return <EditRow key={p.id} preinscription={p} onSaved={() => { setEditingId(null); load(); }} onCancel={() => setEditingId(null)} />;
                  }
                  const progress = getProgress(p);
                  return (
                    <tr key={p.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                      <td className="px-3 py-2.5">
                        <button onClick={() => setDossierId(p.id)} className="font-medium text-gray-900 hover:text-navy-700 hover:underline text-left">{p.enfant_prenom} {p.enfant_nom}</button>
                        {p.parent1_nom && (
                          <div className="text-xs text-gray-400">{p.parent1_prenom} {p.parent1_nom}</div>
                        )}
                        {p.date_naissance && (
                          <div className="text-xs text-gray-300">Né(e) le {formatDate(p.date_naissance)}</div>
                        )}
                      </td>
                      <td className="px-2 py-2.5">
                        {p.structure_nom ? (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{
                            backgroundColor: (p.structure_couleur || '#1e2d4a') + '20',
                            color: p.structure_couleur || '#1e2d4a'
                          }}>{p.structure_nom}</span>
                        ) : <span className="text-xs text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-2.5 text-xs text-gray-600">{p.jours_souhaites || '—'}</td>
                      {ETAPES.map(et => (
                        <td key={et.key} className="px-1 py-2.5 text-center">
                          {p[et.key] ? (
                            <div className="flex flex-col items-center">
                              <Check size={14} className="text-green-500" />
                              <span className="text-[10px] text-gray-400">{formatDate(p[et.key])}</span>
                            </div>
                          ) : (
                            <span className="w-3 h-3 rounded-full bg-gray-200 inline-block" />
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-2.5 text-xs text-gray-600 font-mono">
                        {p.numeros_cheques || '—'}
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        {p.nb_emails > 0 ? (
                          <button onClick={() => setDossierId(p.id)} className="inline-flex flex-col items-center text-xs text-navy-600 hover:text-navy-900" title={`${p.nb_emails_recus || 0} reçu(s), ${p.nb_emails_envoyes || 0} envoyé(s)`}>
                            <span className="inline-flex items-center gap-1">
                              <Mail size={13} className="text-cyan-600" />
                              <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-500"><ArrowDownLeft size={10} className="text-emerald-500" />{p.nb_emails_recus || 0}</span>
                              <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-500"><ArrowUpRight size={10} className="text-blue-500" />{p.nb_emails_envoyes || 0}</span>
                            </span>
                            {p.dernier_email && <span className="text-[10px] text-gray-400">{formatDate(p.dernier_email)}</span>}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${(STATUT_MAP[p.statut] || STATUT_MAP.demande).color}`}>
                          {(STATUT_MAP[p.statut] || STATUT_MAP.demande).label}
                        </span>
                        <div className="mt-1">
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div className="bg-cyan-500 h-1.5 rounded-full transition-all"
                              style={{ width: `${(progress / ETAPES.length) * 100}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-400">{progress}/{ETAPES.length}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setDossierId(p.id)} title="Ouvrir le dossier (appel, visite, convention…)"
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-navy-700">
                            <FolderOpen size={15} />
                          </button>
                          <button onClick={() => setEditingId(p.id)} title="Édition rapide des dates"
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-navy-700">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => supprimer(p)} title="Supprimer cette pré-inscription"
                            className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
